import { ChangeEvent, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import { MEDIA_UPDATE } from '@atom/graph/media';
import { DateTimePicker, Icon, IconButton, TextField } from '@atom/mui';
import { CloseIcon, EditIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { MediaUpdateInput } from '@atom/types/media';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import {
  getDisplayDateTime,
  getUtcDisplayDate,
} from '@atom/utilities/timeUtilities';

import classes from './carousel.module.css';

const SIDE_BAR_CLOSED_WIDTH = '-23.75rem';

const styles = {
  input: {
    color: colors.neutral.white,
  },
  editButton: {
    marginTop: '-5px',
  },
};

interface Props {
  sideBarOpen: boolean;
  onClose: () => void;
  selectedMedia: any;
  canUpdateMedia: boolean;
  refetchMedia?: () => void;
}

const InfoSideBar = ({
  sideBarOpen,
  onClose,
  selectedMedia,
  canUpdateMedia,
  refetchMedia = () => {},
}: Props) => {
  const [editDescription, setEditDescription] = useState<Boolean>(false);
  const [editCapturedDate, setEditCapturedDate] = useState<boolean>(false);
  const [capturedDate, setCapturedDate] = useState<Date>(
    selectedMedia.capturedDate ? new Date(selectedMedia.capturedDate) : null,
  );
  const [description, setDescription] = useState<String>(
    selectedMedia.description || '',
  );

  const [mediaUpdate] = useMutation(MEDIA_UPDATE);

  useEffect(() => {
    setEditDescription(false);
    setEditCapturedDate(false);
  }, [selectedMedia]);

  useEffect(() => {
    setDescription(selectedMedia.description || '');
    setEditDescription(false);
  }, [selectedMedia.description]);

  useEffect(() => {
    setCapturedDate(
      selectedMedia.capturedDate ? new Date(selectedMedia.capturedDate) : null,
    );
    setEditCapturedDate(false);
  }, [selectedMedia.capturedDate]);

  const updateMedia = async (property: keyof MediaUpdateInput, value: any) => {
    await mediaUpdate({
      variables: {
        input: {
          id: selectedMedia?.id,
          [property]: value,
        },
      },
    });

    setEditDescription(false);
    setEditCapturedDate(false);
    refetchMedia();
  };

  const sideBarStyle = {
    marginRight: sideBarOpen ? '0' : SIDE_BAR_CLOSED_WIDTH,
  };

  const updateDescription = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const onDescriptionSave = () => {
    updateMedia('description', description);
  };

  const onCapturedDateSave = () => {
    if (!capturedDate) {
      updateMedia('capturedDate', null);
    } else if (!Number.isNaN(capturedDate.valueOf())) {
      updateMedia('capturedDate', capturedDate.valueOf());
    }
  };

  const toggleCapturedDate = () => {
    setEditCapturedDate(!editCapturedDate);
    setEditDescription(false);
  };

  const toggleDescription = () => {
    setEditDescription(!editDescription);
    setEditCapturedDate(false);
  };

  const onCancel = () => {
    setDescription(selectedMedia.description);
    setCapturedDate(selectedMedia.capturedDate);

    setEditDescription(false);
    setEditCapturedDate(false);
  };

  return (
    <div className={classes.sideBarContainer} style={sideBarStyle}>
      <div className={classes.sideBarHeader}>
        <div className={classes.sideBarHeaderTitle}>Details</div>
        <IconButton size="small" tooltip="Close" onClick={onClose}>
          <Icon color={colors.neutral.white} icon={CloseIcon} />
        </IconButton>
      </div>
      <div className={classes.sideBarContentContainer}>
        <div className={classes.sideBarContentRow}>
          <div className={clsx(classes.sideBarContent, classes.label)}>
            Name:
          </div>
          <div
            className={clsx(classes.sideBarContent, classes.value)}
          >{`${selectedMedia.name}.${selectedMedia.fileExtension}`}</div>
        </div>
        <div className={classes.sideBarContentRow}>
          <div className={clsx(classes.sideBarContent, classes.label)}>
            Uploaded by:
          </div>
          <div className={clsx(classes.sideBarContent, classes.value)}>
            {!R.isEmpty(selectedMedia.createdBy)
              ? textDisplayUtilities.displayName(selectedMedia.createdBy)
              : ''}
          </div>
        </div>
        <div className={classes.sideBarContentRow}>
          <div className={clsx(classes.sideBarContent, classes.label)}>
            Uploaded on:
          </div>
          <div className={clsx(classes.sideBarContent, classes.value)}>
            {getUtcDisplayDate(selectedMedia.createdDate)}
          </div>
        </div>
        <div className={classes.sideBarContentRow}>
          <div className={clsx(classes.sideBarContent, classes.label)}>
            Captured on:
          </div>
          {editCapturedDate ? (
            <div className={clsx(classes.sideBarContent, classes.value)}>
              <div className={classes.inputContainer}>
                <DateTimePicker
                  value={capturedDate}
                  onChange={date => setCapturedDate(date as Date)}
                  inputSx={styles.input}
                />
              </div>
              <div className={classes.buttonContainer}>
                <div
                  className={clsx(classes.button, classes.cancel)}
                  onClick={onCancel}
                >
                  Cancel
                </div>
                <div
                  className={clsx(classes.button, classes.save)}
                  onClick={onCapturedDateSave}
                >
                  Save
                </div>
              </div>
            </div>
          ) : (
            <div className={classes.descriptionRow}>
              {canUpdateMedia && (
                <IconButton
                  size="small"
                  style={styles.editButton}
                  onClick={toggleCapturedDate}
                >
                  <Icon color={colors.neutral.white} icon={EditIcon} />
                </IconButton>
              )}
              <span>
                {getDisplayDateTime(
                  capturedDate ? capturedDate.valueOf() : null,
                )}
              </span>
            </div>
          )}
        </div>
        <div className={classes.sideBarContentRow}>
          <div className={clsx(classes.sideBarContent, classes.label)}>
            Description:
          </div>
          {editDescription ? (
            <div className={clsx(classes.sideBarContent, classes.value)}>
              <div className={classes.inputContainer}>
                <TextField
                  value={description}
                  onChange={updateDescription}
                  multiline
                  variant="standard"
                  slotProps={{ input: { style: styles.input } }}
                />
              </div>
              <div className={classes.buttonContainer}>
                <div
                  className={clsx(classes.button, classes.cancel)}
                  onClick={onCancel}
                >
                  Cancel
                </div>
                <div
                  className={clsx(classes.button, classes.save)}
                  onClick={onDescriptionSave}
                >
                  Save
                </div>
              </div>
            </div>
          ) : (
            <div className={classes.descriptionRow}>
              {canUpdateMedia && (
                <IconButton
                  size="small"
                  style={styles.editButton}
                  onClick={toggleDescription}
                >
                  <Icon color={colors.neutral.white} icon={EditIcon} />
                </IconButton>
              )}
              <span>{description}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoSideBar;
